import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Loader2, ShieldCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { qmbAPI } from '../../lib/api';

export const EditorsDialog = ({ open, onOpenChange }) => {
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await qmbAPI.getEditors();
      setUsers(res.data.users || []);
      setSelected(new Set(res.data.editor_ids || []));
    } catch (e) {
      toast.error('Laden fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const toggle = (id) => setSelected((s) => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const save = async () => {
    setSaving(true);
    try {
      await qmbAPI.setEditors([...selected]);
      toast.success('QMB-Verantwortliche gespeichert');
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const filtered = users.filter((u) => {
    const s = `${u.first_name} ${u.last_name} ${u.email}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="qmb-editors-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> QMB-Verantwortliche</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          Wähle die Personen, die QMB-Infos veröffentlichen dürfen. Administratoren dürfen immer.
        </p>
        <div
          className="flex items-start gap-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/15 p-3"
          data-testid="qmb-editors-warning"
        >
          <AlertTriangle className="h-4 w-4 mt-0.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="text-xs text-amber-800 dark:text-amber-300">
            <strong>Achtung:</strong> Ausgewählte Nicht-Administratoren erhalten damit
            <strong> volle Bearbeitungsrechte</strong> für QMB-Infos – sie können Beiträge
            anlegen, bearbeiten und löschen sowie Bestätigungen einsehen.
          </p>
        </div>
        <Input placeholder="Suchen…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="qmb-editors-search" />
        {loading ? (
          <div className="space-y-2"><Skeleton className="h-40" /></div>
        ) : (
          <ScrollArea className="h-72 rounded border">
            <div className="divide-y">
              {filtered.map((u) => (
                <label key={u.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-secondary/40"
                  data-testid={`qmb-editor-${u.id}`}>
                  <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggle(u.id)} disabled={u.role === 'admin'} />
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{u.last_name}, {u.first_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                  </div>
                  {u.role === 'admin' && <Badge variant="secondary" className="shrink-0">Admin</Badge>}
                </label>
              ))}
            </div>
          </ScrollArea>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={save} disabled={saving} data-testid="qmb-editors-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Speichern ({selected.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
